import { useKitAvailability } from "@/lib/kitAvailability";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, Clock, Flag, Brain, Radio } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import NewsletterSignup from "@/components/NewsletterSignup";
import { useFeatures } from "@/lib/features";
import { HeroKitCard } from "@/components/starterkit/HeroKitCard";
import { responsiveImg } from "@/lib/img";
import { RookieOffer } from "@/components/RookieOffer";

const HERO_ALT = "A youth basketball coach and his players huddle on the court with their hands raised together";
const BLANK = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

const IMG = {
  hero: "https://images.unsplash.com/photo-1520399636535-24741e71b160?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  kyto: "/coach-kyto-sideline-800.webp",
  coach: "https://images.unsplash.com/photo-1631659718597-1b62ad76da3a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  parent: "https://images.unsplash.com/photo-1770230512429-232aaee1b8f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  closing: "https://images.unsplash.com/photo-1506869640319-fe1a24fd76dc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

const SHOWS = [
  { name: "Time Out", label: "Short film", icon: Clock, desc: "The moments that actually change a season.", to: "/series/time-out", cta: "Watch" },
  { name: "Penalty", label: "Op-ed", icon: Flag, desc: "The hard calls in youth sports, argued honestly.", to: "/series/penalty", cta: "Read" },
  { name: "The Mental Rep", label: "Series", icon: Brain, desc: "The game between the ears, one rep at a time.", to: "/series/the-mental-rep", cta: "Read" },
  { name: "After the Whistle", label: "Podcast", icon: Radio, desc: "What happens once the score stops mattering.", to: "/series/after-the-whistle", cta: "Listen" },
];

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Home() {
  const kit = useKitAvailability();
  const features = useFeatures();
  const [lead, setLead] = useState(null);
  const [rows, setRows] = useState([]);

  const labelOf = (a) => (a && a.series && a.series !== "None") ? a.series : (a ? a.category : "");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/articles?limit=5");
      const list = data || [];
      setLead(list[0] || null);
      setRows(list.slice(1, 4));
    })();
  }, []);

  return (
    <div data-testid="home-page">
      <Seo path="/" />

      {/* ===== 1. HERO ===== */}
      <section className="relative bg-[color:var(--vt-dark)] vt-grain overflow-hidden" data-testid="hero">
        {/* desktop bg photo + fade */}
        <div className="hidden lg:block absolute inset-0">
          <picture>
            <source media="(max-width: 1023px)" srcSet={BLANK} />
            <img {...responsiveImg(IMG.hero, "72vw", [800, 1200, 1600])} alt={HERO_ALT} fetchpriority="high" className="absolute right-0 top-0 h-full w-[72%] object-cover" style={{ filter: "grayscale(0.35)" }} />
          </picture>
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, var(--vt-dark) 34%, rgba(17,19,21,0.85) 46%, rgba(17,19,21,0.15) 70%, rgba(17,19,21,0.35) 100%)" }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 lg:min-h-[700px] flex flex-col justify-center pt-12 lg:py-10">
          <div className="lg:max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="h-[2px] w-8 bg-[color:var(--vt-amber)]" />
              <span className="vt-eyebrow">For Rookie Coaches &amp; Youth Sports Parents</span>
            </div>
            <h1 className="mt-6 font-head text-[color:var(--vt-white)] text-[64px] lg:text-[92px] xl:text-[96px] leading-[0.86]">
              FOR EVERYONE<br />WHO GOT<br /><span className="text-[color:var(--vt-amber)]">VOLUNTOLD.</span>
            </h1>
            <p className="mt-6 text-[color:var(--vt-white)] font-bold text-xl lg:text-[22px] leading-snug max-w-xl">
              For the people who show up after everyone else goes home.
            </p>
            <p className="mt-3 text-[color:var(--vt-graphite)] text-sm">Real stories. Better coaching. Stronger athletes.</p>
            <div className="mt-8 max-w-xl border-l-[3px] border-[color:var(--vt-amber)] pl-5" data-testid="hero-starter-kit">
              <span className="vt-eyebrow">The Starter Kit · $9</span>
              <p className="mt-2 text-[color:var(--vt-white)] text-base lg:text-lg leading-snug" data-testid="hero-starter-kit-copy">
                Just got voluntold? Get a practice plan, rotation sheet and first-week scripts built for your team in minutes.
              </p>
              {kit.label && (
                <p className="mt-2 text-sm text-[color:var(--vt-graphite)]" data-testid="hero-starter-kit-availability">
                  Available now: {kit.label}. Coaching another sport?{" "}
                  <Link to="/starter-kit#notify" data-testid="hero-starter-kit-notify" className="underline underline-offset-4 hover:text-[color:var(--vt-amber)]">Join the waitlist</Link>
                </p>
              )}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/starter-kit" data-testid="hero-starter-kit-btn" className="vt-btn vt-btn-primary w-full sm:w-auto">
                {kit.sports.length === 1 ? `Get the ${kit.sports[0]} Starter Kit, $9` : "Get the Starter Kit, $9"} <span className="vt-arrow">→</span>
              </Link>
              <button onClick={() => scrollToId("the-sideline")} data-testid="hero-join-btn" className="vt-btn vt-btn-secondary-dark w-full sm:w-auto">
                Join the Sideline <span className="vt-arrow">→</span>
              </button>
            </div>
            <button onClick={() => scrollToId("the-latest")} data-testid="hero-latest-btn" className="mt-4 text-sm text-[color:var(--vt-graphite)] hover:text-[color:var(--vt-amber)] transition-colors">
              Or read the latest →
            </button>
            <HeroKitCard testId="hero-kit-card-mobile" className="lg:hidden mt-12 mx-auto w-[82%] max-w-[340px]" />
          </div>
        </div>

        {/* desktop sample card */}
        <HeroKitCard testId="hero-kit-card" className="hidden lg:block absolute z-10 bottom-14 right-[16%] xl:right-[19%] w-[300px] xl:w-[330px]" />

        {/* mobile photo + note */}
        <div className="lg:hidden relative mt-10">
          <img {...responsiveImg(IMG.hero, "100vw")} alt={HERO_ALT} loading="lazy" decoding="async" className="w-full h-[400px] object-cover" style={{ filter: "grayscale(0.35)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(17,19,21,0.55) 0%, rgba(17,19,21,0) 40%)" }} />
          <p className="vt-hand absolute top-4 right-4 text-right text-[color:var(--vt-white)] text-2xl leading-tight" style={{ transform: "rotate(-8deg)" }}>
            Nobody<br />signs up<br />for this.<br />You showed<br />up anyway.<span className="block h-[3px] w-24 bg-[color:var(--vt-amber)] ml-auto mt-1" />
          </p>
        </div>

        {/* desktop handwritten note */}
        <p className="vt-hand hidden lg:block absolute top-24 right-12 text-right text-[color:var(--vt-white)] text-3xl leading-tight z-10" style={{ transform: "rotate(-8deg)" }}>
          Nobody<br />signs up<br />for this.<br />You showed<br />up anyway.<span className="block h-[3px] w-28 bg-[color:var(--vt-amber)] ml-auto mt-1" />
        </p>
      </section>

      <RookieOffer source="homepage" />

      {/* ===== 2. THE LATEST ===== */}
      <section id="the-latest" className="bg-[color:var(--vt-dark)] vt-grain border-t border-[color:var(--vt-rule-dark)]" data-testid="the-latest">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="vt-eyebrow">Read &amp; Watch</span>
              <h2 className="mt-2 font-head text-[color:var(--vt-white)] text-5xl lg:text-6xl">THE LATEST</h2>
            </div>
            <Link to="/category/coaches-corner" data-testid="latest-everything-link" className="font-head text-[color:var(--vt-amber)] text-sm tracking-wide hover:text-[color:var(--vt-amber-hover)] transition-colors">EVERYTHING →</Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* lead */}
            {lead && (
              <Link to={`/article/${lead.slug}`} data-testid="latest-lead" className="group block">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img {...responsiveImg(lead.image_url, "(min-width: 1024px) 50vw, 100vw")} alt={lead.title} loading="lazy" decoding="async" className="w-full h-full object-cover vt-photo" />
                  {lead.format === "Watch" && (
                    <span className="absolute top-3 left-3 flex items-center gap-1 bg-[color:var(--vt-amber)] text-[color:var(--vt-dark)] font-head uppercase text-[10px] tracking-wide px-2 py-1"><Play size={12} fill="currentColor" /> Watch</span>
                  )}
                </div>
                <span className="inline-block mt-4 bg-[color:var(--vt-amber)] text-[color:var(--vt-dark)] font-[Nunito_Sans] font-extrabold text-[11px] uppercase tracking-[0.14em] px-2 py-1">{labelOf(lead)}</span>
                <h3 className="mt-3 font-head text-[color:var(--vt-white)] text-4xl lg:text-[70px] leading-[0.9] group-hover:text-[color:var(--vt-amber)] transition-colors">{lead.title}</h3>
                <p className="mt-3 text-[color:var(--vt-body-on-dark)] text-base leading-relaxed line-clamp-2">{lead.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-2 font-head text-[color:var(--vt-amber)] text-sm">READ MORE <ArrowRight size={15} /></span>
              </Link>
            )}

            {/* 3 rows */}
            <div className="flex flex-col divide-y divide-[color:var(--vt-rule-dark)]">
              {rows.map((a, i) => (
                <Link key={a.slug} to={`/article/${a.slug}`} data-testid={`latest-row-${i}`} className="group flex gap-4 py-5 first:pt-0">
                  <div className="relative w-24 h-24 lg:w-28 lg:h-28 shrink-0 overflow-hidden">
                    <img {...responsiveImg(a.image_url, "112px", [480])} alt={a.title} loading="lazy" decoding="async" className="w-full h-full object-cover vt-photo" />
                    {a.format === "Watch" && (
                      <span className="absolute top-1 left-1 flex items-center justify-center w-6 h-6 bg-[color:var(--vt-amber)] text-[color:var(--vt-dark)]"><Play size={12} fill="currentColor" /></span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <span className="vt-eyebrow" style={{ color: "var(--vt-amber)" }}>{labelOf(a)}</span>
                    <h4 className="mt-1 font-head text-[color:var(--vt-white)] text-2xl lg:text-[34px] leading-[0.95] group-hover:text-[color:var(--vt-amber)] transition-colors line-clamp-2">{a.title}</h4>
                    <p className="mt-1 text-[color:var(--vt-graphite)] text-sm line-clamp-1">{a.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== 3. THE SIDELINE ===== */}
      <section id="the-sideline" className="vt-paper" data-testid="the-sideline">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12 items-start">
          {/* photo */}
          <div className="relative">
            <div className="aspect-[3/4] overflow-hidden border-2 border-[color:var(--vt-dark)]">
              <img src={IMG.kyto} srcSet="/coach-kyto-sideline-480.webp 480w, /coach-kyto-sideline-800.webp 800w" sizes="(min-width: 1024px) 400px, 100vw" alt="Coach Kyto" loading="lazy" decoding="async" className="w-full h-full object-cover" style={{ filter: "grayscale(1)" }} />
            </div>
            <span className="mt-3 block vt-eyebrow vt-eyebrow--cream">Coach Kyto · Editor</span>
          </div>
          {/* text */}
          <div className="min-w-0">
            <span className="vt-eyebrow vt-eyebrow--cream">A Weekly Note from Coach Kyto</span>
            <h2 className="mt-3 font-head text-[color:var(--vt-body-on-cream)] text-6xl lg:text-[84px] xl:text-[100px] leading-[0.86]">
              <span className="vt-brush">THE SIDELINE</span>
            </h2>
            <p className="mt-8 text-[color:var(--vt-body-on-cream)] text-lg leading-relaxed">
              "I've spent years around youth sports. Voluntold is about everything happening around the scoreboard that deserves more attention."
            </p>
            <p className="vt-hand text-[color:var(--vt-body-on-cream)] text-2xl mt-2">— Coach Kyto</p>
            <p className="mt-6 text-[color:var(--vt-label-on-cream)] text-sm leading-relaxed">
              One read worth taking into next week: a real story, practical coaching insight, and a mental game lesson.
            </p>
          </div>
          {/* signup card */}
          <div className="bg-[#FBF9F5] border-2 border-[color:var(--vt-dark)] p-6" style={{ boxShadow: "10px 10px 0 var(--vt-dark)" }}>
            <h3 className="font-head text-[color:var(--vt-body-on-cream)] text-2xl leading-tight">GET THE SIDELINE EVERY WEEK.</h3>
            <div className="mt-4">
              <NewsletterSignup source="homepage" tone="light" showCopy={false} showRole testId="sideline-newsletter" />
            </div>
            <p className="mt-3 vt-eyebrow vt-eyebrow--cream" style={{ fontSize: "10px" }}>No spam. Just good stuff.</p>
          </div>
        </div>
      </section>

      {/* ===== 4. SHOWS & SERIES ===== */}
      <section className="bg-[color:var(--vt-dark)] vt-grain" data-testid="shows-series">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <span className="vt-eyebrow">Shows &amp; Series</span>
          <h2 className="mt-2 mb-10 font-head text-[color:var(--vt-white)] text-5xl lg:text-6xl">ONLY ON VOLUNTOLDHQ</h2>
          <div className="flex lg:grid lg:grid-cols-4 gap-5 overflow-x-auto pb-2 -mx-5 px-5 lg:mx-0 lg:px-0 lg:overflow-visible snap-x">
            {SHOWS.map((s) => {
              const Icon = s.icon;
              const slug = s.to.split("/").pop();
              return (
                <div key={s.name}
                  className="group relative shrink-0 w-[272px] lg:w-auto snap-start border-2 border-[color:var(--vt-rule-dark)] hover:border-[color:var(--vt-amber)] hover:-translate-y-[3px] transition-all overflow-hidden">
                  <Link to={s.to} data-testid={`show-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="block relative h-64 p-6 flex flex-col justify-end">
                    <img {...responsiveImg(IMG.hero, "(min-width: 1024px) 25vw, 272px", [480, 800])} alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "grayscale(1)", opacity: 0.2 }} />
                    <div className="relative">
                      <span className="vt-eyebrow" style={{ color: "var(--vt-graphite)" }}>{s.label}</span>
                      <Icon size={26} className="mt-3 text-[color:var(--vt-amber)]" strokeWidth={1.5} />
                      <h3 className="mt-2 font-head text-[color:var(--vt-white)] text-3xl leading-none">{s.name}</h3>
                      <p className="mt-2 text-[color:var(--vt-graphite)] text-sm">{s.desc}</p>
                      <span className="mt-3 inline-flex items-center gap-1 font-head text-[color:var(--vt-amber)] text-xs uppercase tracking-wide">{s.cta} <ArrowRight size={13} /></span>
                    </div>
                  </Link>
                  <Link to={`/rulebook#${slug}`} data-testid={`whats-this-${slug}`}
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    className="absolute top-3 right-3 z-20 text-[10px] tracking-[0.15em] text-[color:var(--vt-graphite)] hover:text-[color:var(--vt-amber)] transition-colors">
                    WHAT'S THIS?
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== 5. START HERE ===== */}
      <section className="vt-paper" data-testid="start-here">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <span className="vt-eyebrow vt-eyebrow--cream">Free Tools</span>
          <h2 className="mt-2 mb-3 font-head text-[color:var(--vt-body-on-cream)] text-5xl lg:text-6xl">START HERE.</h2>
          <Link to="/quick-plays" data-testid="start-here-quick-plays" className="inline-block mb-10 link-line text-[color:var(--vt-body-on-cream)] font-semibold">Need help right now? → Quick Plays</Link>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* coaches */}
            <div className="bg-[#FBF9F5] border-2 border-[color:var(--vt-dark)] group" data-testid="start-coaches">
              <div className="aspect-[16/9] overflow-hidden border-b-2 border-[color:var(--vt-dark)]">
                <img {...responsiveImg(IMG.coach, "(min-width: 768px) 50vw, 100vw")} alt="For coaches" loading="lazy" decoding="async" className="w-full h-full object-cover object-[center_18%] vt-photo" />
              </div>
              <div className="p-7">
                <span className="vt-eyebrow vt-eyebrow--cream">For Coaches</span>
                <h3 className="mt-2 font-head text-[color:var(--vt-body-on-cream)] text-3xl leading-none">Coach Readiness Assessment</h3>
                <p className="mt-3 text-[color:var(--vt-body-on-cream)] text-sm leading-relaxed">Got handed a clipboard? Find out where you stand before the first practice.</p>
                <Link to="/assessment" data-testid="start-coaches-cta" className="vt-btn vt-btn-secondary-cream mt-5 w-full sm:w-auto">Take the Assessment <span className="vt-arrow">→</span></Link>
                {features?.coach_ready
                  ? <Link to="/coach-ready" className="mt-4 block link-line w-fit text-sm" data-testid="home-coach-ready-link">Also: Coach Ready, six lessons for your first season →</Link>
                  : <Link to="/shop" className="mt-4 block link-line w-fit text-sm">Also: the Volun'told Coaching Kit →</Link>}
              </div>
            </div>
            {/* parents */}
            <div className="bg-[#FBF9F5] border-2 border-[color:var(--vt-dark)] group" data-testid="start-parents">
              <div className="aspect-[16/9] overflow-hidden border-b-2 border-[color:var(--vt-dark)]">
                <img {...responsiveImg(IMG.parent, "(min-width: 768px) 50vw, 100vw")} alt="For parents" loading="lazy" decoding="async" className="w-full h-full object-cover vt-photo" />
              </div>
              <div className="p-7">
                <span className="vt-eyebrow vt-eyebrow--cream">For Parents</span>
                <h3 className="mt-2 font-head text-[color:var(--vt-body-on-cream)] text-3xl leading-none">Parent Script Builder</h3>
                <p className="mt-3 text-[color:var(--vt-body-on-cream)] text-sm leading-relaxed">The right words for playing time, quitting, and the car ride home.</p>
                <Link to="/tools/parent-script" data-testid="start-parents-cta" className="vt-btn vt-btn-secondary-cream mt-5 w-full sm:w-auto">Build Your Script <span className="vt-arrow">→</span></Link>
                <Link to="/shop" className="mt-4 block link-line w-fit text-sm">Also: The Youth Sports Parent Playbook →</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 6. HOT TAKE HUDDLE ===== */}
      <HotTakeHuddle />

      {/* ===== 7. NOMINATE ===== */}
      <section className="vt-paper" data-testid="nominate-cta">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 max-w-4xl">
          <span className="vt-eyebrow vt-eyebrow--cream">Recognition</span>
          <h2 className="mt-3 font-head text-[color:var(--vt-body-on-cream)] text-5xl lg:text-7xl leading-[1.05]" data-testid="nominate-headline">
            KNOW SOMEONE WHO GOT <span className="inline-block bg-[color:var(--vt-amber)] text-[color:var(--vt-dark)] px-2 pt-1 mt-1 leading-[0.95]">VOLUNTOLD?</span>
          </h2>
          <p className="mt-6 text-[color:var(--vt-body-on-cream)] text-lg leading-relaxed max-w-2xl">
            The coach, team parent, ref, or volunteer who keeps showing up. Tell us their story — we'll tell everyone else.
          </p>
          <p className="vt-hand text-[color:var(--vt-body-on-cream)] text-2xl mt-4">they never signed up for this.</p>
          <Link to="/nominate" data-testid="nominate-cta-btn" className="vt-btn vt-btn-secondary-cream mt-7 w-full sm:w-auto">Nominate Someone <span className="vt-arrow">→</span></Link>
        </div>
      </section>

      {/* ===== 8. CLOSING ===== */}
      <section className="relative bg-[color:var(--vt-dark)] vt-grain overflow-hidden" data-testid="closing">
        <img {...responsiveImg(IMG.closing, "100vw", [800, 1200, 1600])} alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "grayscale(1)", opacity: 0.2 }} />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <h2 className="font-head text-[color:var(--vt-white)] text-[84px] lg:text-[176px] leading-[0.84]">
            MORE THAN<br /><span className="text-[color:var(--vt-amber)]">THE SCORE.</span>
          </h2>
          <p className="mt-8 text-[color:var(--vt-body-on-dark)] text-lg leading-relaxed max-w-2xl">
            We believe in better humans through sports — by supporting coaches, empowering parents, and investing in the next generation of athletes.
          </p>
          <div className="mt-10 max-w-xl">
            <span className="vt-eyebrow">One read a week. From the sideline.</span>
            <div className="mt-3">
              <NewsletterSignup source="homepage-closing" tone="dark" showCopy={false} testId="closing-newsletter" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function HotTakeHuddle() {
  const [poll, setPoll] = useState(null);
  const [voted, setVoted] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/poll");
        if (data) {
          setPoll(data);
          const prev = localStorage.getItem(`vt_poll_vote_${data.id}`);
          if (prev === "a" || prev === "b") setVoted(prev);
        }
      } catch (e) { /* no active poll */ }
    })();
  }, []);

  const vote = async (choice) => {
    if (voted || busy || !poll) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/poll/${poll.id}/vote`, { choice });
      setPoll(data);
      localStorage.setItem(`vt_poll_vote_${poll.id}`, choice);
      setVoted(choice);
    } catch (e) { /* ignore */ } finally { setBusy(false); }
  };

  if (!poll) return null;

  const Bar = ({ side }) => {
    const label = side === "a" ? poll.a_label : poll.b_label;
    const sub = side === "a" ? poll.a_sub : poll.b_sub;
    const pct = side === "a" ? poll.a_pct : poll.b_pct;
    const mine = voted === side;
    return (
      <div data-testid={`hot-take-result-${side}`} className="relative border-2 border-[color:var(--vt-rule-dark)] overflow-hidden">
        <div className="absolute inset-y-0 left-0 transition-all duration-700" style={{ width: `${pct}%`, background: mine ? "var(--vt-amber)" : "#262626" }} />
        <div className="relative flex items-center justify-between px-5 py-5">
          <div className="text-left">
            <p className={`font-head text-2xl leading-none ${mine ? "text-[color:var(--vt-dark)]" : "text-[color:var(--vt-white)]"}`}>{label}</p>
            {sub && <p className={`text-xs mt-1 ${mine ? "text-[color:var(--vt-dark)]" : "text-[color:var(--vt-graphite)]"}`}>{sub}</p>}
          </div>
          <span className={`font-head text-2xl ${mine ? "text-[color:var(--vt-dark)]" : "text-[color:var(--vt-white)]"}`}>{pct}%</span>
        </div>
      </div>
    );
  };

  return (
    <section className="bg-[color:var(--vt-dark)] vt-grain border-t border-[color:var(--vt-rule-dark)]" data-testid="hot-take-huddle">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 text-center">
        <span className="vt-eyebrow">Hot Take Huddle · This Week</span>
        <h2 className="mt-3 font-head text-[color:var(--vt-white)] text-4xl lg:text-6xl leading-[0.95] max-w-3xl mx-auto">{poll.question.toUpperCase()}</h2>
        <Link to="/rulebook#hot-take-huddle" data-testid="poll-how-this-works" className="mt-3 inline-block font-[IBM_Plex_Mono] text-xs tracking-[0.15em] text-[color:var(--vt-graphite)] hover:text-[color:var(--vt-amber)] transition-colors">HOW THIS WORKS →</Link>

        {!voted ? (
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto">
            <button onClick={() => vote("a")} disabled={busy} data-testid="hot-take-yes" className="vt-btn vt-btn-secondary-dark w-full flex-1 !h-auto !min-h-[60px] py-4 leading-tight flex-col">
              <span>{poll.a_label}</span>{poll.a_sub && <span className="text-xs font-normal opacity-80">{poll.a_sub}</span>}
            </button>
            <button onClick={() => vote("b")} disabled={busy} data-testid="hot-take-no" className="vt-btn vt-btn-secondary-dark w-full flex-1 !h-auto !min-h-[60px] py-4 leading-tight flex-col">
              <span>{poll.b_label}</span>{poll.b_sub && <span className="text-xs font-normal opacity-80">{poll.b_sub}</span>}
            </button>
          </div>
        ) : (
          <div className="mt-8 max-w-2xl mx-auto space-y-4" data-testid="hot-take-results">
            <Bar side="a" />
            <Bar side="b" />
            <p className="text-[color:var(--vt-graphite)] text-sm">{poll.total} {poll.total === 1 ? "vote" : "votes"} so far.</p>
            <button onClick={() => scrollToId("the-sideline")} data-testid="hot-take-breakdown" className="inline-flex items-center gap-2 font-head uppercase tracking-wide text-sm text-[color:var(--vt-amber)] hover:text-[color:var(--vt-amber-hover)] transition-colors">
              Coach Kyto's take lands in this week's Sideline. Get the breakdown <ArrowRight size={15} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

